"use client";

import { FormEvent, useState } from "react";
import forgotPassword from "@/lib/auth/forgotPassword";
import AlertBox from "@/app/[locale]/auth/login/components/UI/AlertBox";
import { cleanUpHtml } from "@/app/[locale]/auth/login/components/utils";
import { validateEmail } from "@/lib/auth/validateRegistrationFields";
import { Button } from "@/components/ui/button";

interface ResetPasswordRequestFormProps {
  isModal?: boolean;
}

export default function ResetPasswordRequestForm({
  isModal = false,
}: ResetPasswordRequestFormProps) {
  const [showAlert, setShowAlert] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [emailValidationError, setEmailValidationError] = useState("");

  function validateEmailField(email: string) {
    const error = validateEmail(email);
    if (error) {
      setEmailValidationError(error);
    } else {
      setEmailValidationError("");
    }
  }

  const handleResetPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (emailValidationError) {
      return;
    }

    const result = await forgotPassword({ email });

    if (result.success) {
      setIsSuccess(true);
      setShowAlert(true);
    } else {
      setIsSuccess(false);
      setErrorMessage(cleanUpHtml(result.value));
      setShowAlert(true);
    }
  };

  return (
    <div className="w-full md:max-w-[628px] flex flex-col gap-6">
      {showAlert && isSuccess && (
        <div className="bg-[#CFE5DB] p-6 rounded-[8px] border border-putty-400 flex flex-col gap-4">
          <h3 className="text-base md:text-lg my-0 font-bold text-gray-800">
            Check your email
          </h3>
          <p className="text-base md:text-lg my-0 text-gray-800">
            We sent an email to {email} with instructions to reset your
            password. If you do not receive a password reset message after 1
            minute, verify that you entered the correct email address, or check
            your spam folder. If you need further assistance,{" "}
            <a
              href="https://support.edx.org/hc/en-us/articles/206212088-What-if-I-did-not-receive-a-password-reset-message"
              className="text-info-500 underline hover:text-[#003041]"
            >
              contact technical support
            </a>
            .
          </p>
        </div>
      )}

      {showAlert && !isSuccess && (
        <AlertBox type="error" title="Error" message={errorMessage} />
      )}

      {!isModal && (
        <h2 className="text-3xl md:text-4xl my-0 italic font-black">
          Reset password
        </h2>
      )}

      <p className="text-base md:text-lg text-gray-700 mb-0">
        Please enter your email address below and we will send you an email with
        instructions on how to reset your password.
      </p>

      <form onSubmit={handleResetPassword} className="flex flex-col gap-6">
        <div>
          <label className="text-md font-bold text-gray-800" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="w-full px-4 py-[9px] mb-0 border text-sm text-gray-700 box-border focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 rounded-[6px]"
            placeholder=""
            name="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={(e) => validateEmailField(e.target.value)}
          />
          {emailValidationError && (
            <p className="text-sm text-red-600 mt-1">{emailValidationError}</p>
          )}
        </div>

        <Button type="submit" className="text-md w-fit" variant="brand">
          Submit
        </Button>
      </form>

      <div className="text-gray-700 flex flex-col gap-6">
        <a
          className="inline-flex items-center text-left text-sm md:text-md text-info-500 underline underline-offset-2 decoration-[#006daa4d] hover:text-[#003041] hover:decoration-[#003041]"
          href="https://help.edx.org/edxlearner/s/topic/0TOQq0000001UdZOAU/account-basics"
        >
          Need help signing in?
        </a>
        <p className="my-0 text-base md:text-lg">
          For additional help, contact edX support at&nbsp;
          <a
            className="inline-flex items-center text-left text-info-500 underline underline-offset-2 decoration-[#006daa4d] hover:text-[#003041] hover:decoration-[#003041]"
            href="mailto:info@edx.org"
          >
            info@edx.org
          </a>
        </p>
      </div>
    </div>
  );
}
